import { useRef, useState } from "react";
import { useFormik, FieldArray, FormikProvider } from "formik";
import * as Yup from "yup";
import "./AddCandidate.css";
import { getCandidateList } from "./Service/RecruitmentService";

/* ─────────────────────────────────────────────
   Static lookup options (map label -> id expected by API)
───────────────────────────────────────────── */
const WORK_MODE_OPTIONS = [
  { label: "Any", id: 0 },
  { label: "Remote only", id: 1 },
  { label: "Hybrid", id: 2 },
  { label: "On-site", id: 3 },
];

const NOTICE_PERIOD_OPTIONS = [
  { label: "Immediate", days: 0 },
  { label: "15 days", days: 15 },
  { label: "30 days", days: 30 },
  { label: "45 days", days: 45 },
  { label: "60 days", days: 60 },
  { label: "90 days", days: 90 },
];

const SOURCE_OPTIONS = [
  { label: "HR uploaded", id: 1 },
  { label: "Referral", id: 2 },
  { label: "Job portal — Naukri", id: 3 },
  { label: "Job portal — LinkedIn", id: 4 },
  { label: "Job portal — Indeed", id: 5 },
  { label: "Walkup / Direct", id: 6 },
];

const JOB_OPTIONS = [
  { label: "JOB-2026-001 — Senior Frontend Developer", id: 1 },
  { label: "JOB-2026-002 — Product Manager", id: 2 },
  { label: "JOB-2026-004 — Data Analyst", id: 4 },
  { label: "JOB-2026-005 — Senior Backend Engineer", id: 5 },
];

const STAGE_OPTIONS = [
  { label: "Applied", id: 1 },
  { label: "Screening", id: 2 },
  { label: "Interview", id: 3 },
];

const SECTIONS = [
  { id: "s-personal", label: "Personal Info", icon: "fa-user" },
  { id: "s-contact", label: "Contact", icon: "fa-phone" },
  { id: "s-resume", label: "Resume", icon: "fa-file-pdf" },
  { id: "s-skills", label: "Skills", icon: "fa-tags" },
  { id: "s-exp", label: "Experience", icon: "fa-briefcase" },
  { id: "s-edu", label: "Education", icon: "fa-graduation-cap" },
  { id: "s-links", label: "Links", icon: "fa-globe" },
  { id: "s-job", label: "Link to Job", icon: "fa-link" },
];

/* ─────────────────────────────────────────────
   Toast helper (same pattern as rest of the app)
───────────────────────────────────────────── */
function showToast(msg, type) {
  const t = document.createElement("div");
  t.className = "og-toast";
  const color = type === "error" ? "#f87171" : type === "warn" ? "#fbbf24" : "#4ade80";
  const icon =
    type === "error" ? "fa-times-circle" : type === "warn" ? "fa-triangle-exclamation" : "fa-check-circle";
  t.innerHTML = `<i class="fa-solid ${icon}" style="color:${color};"></i>${msg}`;
  document.body.appendChild(t);
  setTimeout(() => {
    t.style.opacity = "0";
    t.style.transition = "opacity .3s";
    setTimeout(() => t.remove(), 300);
  }, 2800);
}

/* ─────────────────────────────────────────────
   Validation Schema
───────────────────────────────────────────── */
const validationSchema = Yup.object({
  firstName: Yup.string().trim().required("First name is required"),
  lastName: Yup.string().trim().required("Last name is required"),
  currentRole: Yup.string(),
  currentCompany: Yup.string(),
  totalExperienceYears: Yup.number()
    .typeError("Enter a valid number")
    .min(0, "Cannot be negative")
    .required("Total experience is required"),
  location: Yup.string().trim().required("Location is required"),
  workMode: Yup.string(),
  expectedCTC: Yup.string(),
  noticePeriod: Yup.string(),
  aboutSummary: Yup.string(),

  email: Yup.string().trim().email("Enter a valid email").required("Email is required"),
  phone: Yup.string().trim().required("Phone is required"),
  alternatePhone: Yup.string(),
  source: Yup.string().required("Source is required"),

  skills: Yup.array().of(Yup.string()).min(1, "Add at least one skill"),

  experience: Yup.array().of(
    Yup.object({
      company: Yup.string(),
      role: Yup.string(),
      from: Yup.string(),
      to: Yup.string(),
    })
  ),

  education: Yup.array().of(
    Yup.object({
      degree: Yup.string(),
      institution: Yup.string(),
      year: Yup.string(),
      grade: Yup.string(),
    })
  ),

  linkedin: Yup.string().url("Enter a valid URL").nullable().notRequired(),
  github: Yup.string().url("Enter a valid URL").nullable().notRequired(),
  portfolio: Yup.string().url("Enter a valid URL").nullable().notRequired(),

  jobId: Yup.string(),
  initialStage: Yup.string(),
});

const initialValues = {
  firstName: "",
  lastName: "",
  currentRole: "",
  currentCompany: "",
  totalExperienceYears: "",
  location: "",
  workMode: "Any",
  expectedCTC: "",
  noticePeriod: "Immediate",
  aboutSummary: "",

  email: "",
  phone: "",
  alternatePhone: "",
  source: "",

  skills: ["React", "TypeScript", "Next.js"],

  experience: [{ company: "", role: "", from: "", to: "" }],
  education: [{ degree: "", institution: "", year: "", grade: "" }],

  linkedin: "",
  github: "",
  portfolio: "",

  jobId: "",
  initialStage: "Applied",
};

export default function AddCandidate() {
  const [activeSection, setActiveSection] = useState("s-personal");
  const [skillInput, setSkillInput] = useState("");
  const [resumeFile, setResumeFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fileInputRef = useRef(null);
  const navigate = (href) => {
    window.location.href = href;
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      await handleSave(values);
    },
  });

  const goSec = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveSection(id);
  };

  /* ---------- Skills tag input ---------- */
  const addSkill = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const val = skillInput.trim().replace(",", "");
      if (!val) return;
      if (!formik.values.skills.includes(val)) {
        formik.setFieldValue("skills", [...formik.values.skills, val]);
      }
      setSkillInput("");
    }
  };
  const removeSkill = (idx) => {
    const next = [...formik.values.skills];
    next.splice(idx, 1);
    formik.setFieldValue("skills", next);
  };

  /* ---------- Resume upload ---------- */
  const handleFileSelect = (file) => {
    if (!file) return;
    setResumeFile(file);
  };
  const removeFile = () => {
    setResumeFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files.length) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  /* ---------- Build API payload ---------- */
  const buildPayload = (values) => {
    const workMode = WORK_MODE_OPTIONS.find((w) => w.label === values.workMode);
    const notice = NOTICE_PERIOD_OPTIONS.find((n) => n.label === values.noticePeriod);
    const source = SOURCE_OPTIONS.find((s) => s.label === values.source);
    const job = JOB_OPTIONS.find((j) => j.label === values.jobId);
    const stage = STAGE_OPTIONS.find((s) => s.label === values.initialStage);

    const ctcVal = values.expectedCTC ? Number(values.expectedCTC) || 0 : 0;

    return {
      Id: 0,
      FirstName: values.firstName,
      LastName: values.lastName,
      Email: values.email,
      Phone: values.phone,
      AlternatePhone: values.alternatePhone || "",
      Location: values.location,
      CurrentRole: values.currentRole || "",
      CurrentCompany: values.currentCompany || "",
      TotalExperienceYears: Number(values.totalExperienceYears) || 0,
      ExpectedCTCMin: ctcVal,
      ExpectedCTCMax: ctcVal,
      CTCCurrencyCode: "INR",
      NoticePeriodDays: notice ? notice.days : 0,
      WorkModePreferenceId: workMode ? workMode.id : 0,
      CandidateSourceId: source ? source.id : 0,
      ResumeFileName: resumeFile ? resumeFile.name : "",
      ResumeFilePath: "",
      AboutSummary: values.aboutSummary || "",
      ATSPortalEmail: "",
      SkillsPrimary: values.skills.join(","),
      SkillsOther: "",
      Links: JSON.stringify({
        linkedin: values.linkedin || "",
        github: values.github || "",
        portfolio: values.portfolio || "",
      }),
      JobId: job ? job.id : 0,
      InitialStageId: stage ? stage.id : 0,
    };
  };

  /* ---------- Save / submit ---------- */
  async function handleSave(values) {
    setSubmitting(true);
    const params = buildPayload(values);
    try {
      let res = await getCandidateList(params);
      if (res?.Status === "Success" || res?.StatusCode === 2000) {
        showToast("Candidate saved and added to database");
        setTimeout(() => navigate("recruitment-candidates.html"), 1500);
      } else {
        showToast(res?.Message || "Something went wrong", "error");
      }
    } catch (error) {
      showToast("Failed to save candidate", "error");
    } finally {
      setSubmitting(false);
    }
  }

  const err = (name) => {
    const path = name.split(".");
    let touched = formik.touched;
    let errors = formik.errors;
    for (const p of path) {
      touched = touched?.[p];
      errors = errors?.[p];
    }
    return touched && errors ? errors : null;
  };

  return (
    <>
    

      <div className="main-bx">
        <div className="topbar">
          <i className="fa-solid fa-user-plus" style={{ color: "var(--primary)" }} />
          <div className="breadcrumb breadcrumb-tight">
            <a href="recruitment-candidates.html">Candidates</a>
            <i className="fa-solid fa-chevron-right" style={{ fontSize: ".6rem" }} />
            <strong style={{ color: "var(--text)" }}>Add Candidate</strong>
          </div>
          <div className="top-right">
            <div className="user-mini">
              <div className="top-avatar">AM</div>
              <div>
                <div className="user-name">Arun Mishra</div>
                <div className="user-role">HR Admin</div>
              </div>
            </div>
          </div>
        </div>

        <div className="page-body page-body-padded">
          <div className="page-head">
            <div>
              <h2 className="page-title">
                <i className="fa-solid fa-user-plus" />
                Add Candidate
              </h2>
              <div className="breadcrumb">Manually add a candidate to the shared database</div>
            </div>
            <button
              className="btn btn-outline btn-sm"
              onClick={() => navigate("recruitment-candidates.html")}
            >
              <i className="fa-solid fa-arrow-left" /> Back
            </button>
          </div>

          <form onSubmit={formik.handleSubmit} noValidate>
            <div className="form-layout">
              <div className="form-nav">
                {SECTIONS.map((s) => (
                  <div
                    key={s.id}
                    className={`fn-item${activeSection === s.id ? " active" : ""}`}
                    onClick={() => goSec(s.id)}
                  >
                    <i className={`fa-solid ${s.icon}`} /> {s.label}
                  </div>
                ))}
              </div>

              <div className="form-sections">
                {/* ── Personal Info ── */}
                <div className="fs-recuriment" id="s-personal">
                  <div className="fs-head">
                    <i className="fs-icon fa-solid fa-user" />
                    <h4>Personal Information</h4>
                  </div>
                  <div className="fs-body">
                    <div className="form-row form-row-2">
                      <div className="fg">
                        <label>
                          First Name <span className="req">*</span>
                        </label>
                        <input
                          className={`og-input${err("firstName") ? " has-error" : ""}`}
                          name="firstName"
                          placeholder="Rahul"
                          value={formik.values.firstName}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {err("firstName") && <div className="field-error">{err("firstName")}</div>}
                      </div>
                      <div className="fg">
                        <label>
                          Last Name <span className="req">*</span>
                        </label>
                        <input
                          className={`og-input${err("lastName") ? " has-error" : ""}`}
                          name="lastName"
                          placeholder="Kumar"
                          value={formik.values.lastName}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {err("lastName") && <div className="field-error">{err("lastName")}</div>}
                      </div>
                    </div>

                    <div className="form-row form-row-3">
                      <div className="fg">
                        <label>Current Role</label>
                        <input
                          className="og-input"
                          name="currentRole"
                          placeholder="Senior Frontend Dev"
                          value={formik.values.currentRole}
                          onChange={formik.handleChange}
                        />
                      </div>
                      <div className="fg">
                        <label>Current Company</label>
                        <input
                          className="og-input"
                          name="currentCompany"
                          placeholder="ABC Corp"
                          value={formik.values.currentCompany}
                          onChange={formik.handleChange}
                        />
                      </div>
                      <div className="fg">
                        <label>
                          Total Experience <span className="req">*</span>
                        </label>
                        <div className="ctc-row">
                          <input
                            className={`og-input${err("totalExperienceYears") ? " has-error" : ""}`}
                            type="number"
                            min="0"
                            name="totalExperienceYears"
                            placeholder="5"
                            style={{ textAlign: "center" }}
                            value={formik.values.totalExperienceYears}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          <span>years</span>
                        </div>
                        {err("totalExperienceYears") && (
                          <div className="field-error">{err("totalExperienceYears")}</div>
                        )}
                      </div>
                    </div>

                    <div className="form-row form-row-2">
                      <div className="fg">
                        <label>
                          Location <span className="req">*</span>
                        </label>
                        <input
                          className={`og-input${err("location") ? " has-error" : ""}`}
                          name="location"
                          placeholder="Delhi, India"
                          value={formik.values.location}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {err("location") && <div className="field-error">{err("location")}</div>}
                      </div>
                      <div className="fg">
                        <label>Work Mode Preference</label>
                        <select
                          className="og-select"
                          name="workMode"
                          value={formik.values.workMode}
                          onChange={formik.handleChange}
                        >
                          {WORK_MODE_OPTIONS.map((w) => (
                            <option key={w.id} value={w.label}>
                              {w.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="form-row form-row-2">
                      <div className="fg">
                        <label>Expected CTC</label>
                        <input
                          className="og-input"
                          name="expectedCTC"
                          placeholder="e.g. 16"
                          value={formik.values.expectedCTC}
                          onChange={formik.handleChange}
                        />
                      </div>
                      <div className="fg">
                        <label>Notice Period</label>
                        <select
                          className="og-select"
                          name="noticePeriod"
                          value={formik.values.noticePeriod}
                          onChange={formik.handleChange}
                        >
                          {NOTICE_PERIOD_OPTIONS.map((n) => (
                            <option key={n.label} value={n.label}>
                              {n.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="form-row form-row-1">
                      <div className="fg">
                        <label>
                          Brief Summary <span className="label-optional">(optional)</span>
                        </label>
                        <textarea
                          className="og-textarea"
                          rows="3"
                          name="aboutSummary"
                          placeholder="Short professional summary about the candidate…"
                          value={formik.values.aboutSummary}
                          onChange={formik.handleChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Contact ── */}
                <div className="fs-recuriment" id="s-contact">
                  <div className="fs-head">
                    <i className="fs-icon fa-solid fa-phone" />
                    <h4>Contact Details</h4>
                  </div>
                  <div className="fs-body">
                    <div className="form-row form-row-2">
                      <div className="fg">
                        <label>
                          Email <span className="req">*</span>
                        </label>
                        <input
                          className={`og-input${err("email") ? " has-error" : ""}`}
                          type="email"
                          name="email"
                          placeholder="rahul.kumar@gmail.com"
                          value={formik.values.email}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {err("email") && <div className="field-error">{err("email")}</div>}
                      </div>
                      <div className="fg">
                        <label>
                          Phone <span className="req">*</span>
                        </label>
                        <input
                          className={`og-input${err("phone") ? " has-error" : ""}`}
                          name="phone"
                          placeholder="+91 99001 23456"
                          value={formik.values.phone}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {err("phone") && <div className="field-error">{err("phone")}</div>}
                      </div>
                    </div>
                    <div className="form-row form-row-2">
                      <div className="fg">
                        <label>Alternate Phone</label>
                        <input
                          className="og-input"
                          name="alternatePhone"
                          placeholder="Optional"
                          value={formik.values.alternatePhone}
                          onChange={formik.handleChange}
                        />
                      </div>
                      <div className="fg">
                        <label>
                          Source <span className="req">*</span>
                        </label>
                        <select
                          className={`og-select${err("source") ? " has-error" : ""}`}
                          name="source"
                          value={formik.values.source}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        >
                          <option value="">Select source…</option>
                          {SOURCE_OPTIONS.map((s) => (
                            <option key={s.id} value={s.label}>
                              {s.label}
                            </option>
                          ))}
                        </select>
                        {err("source") && <div className="field-error">{err("source")}</div>}
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Resume ── */}
                <div className="fs-recuriment" id="s-resume">
                  <div className="fs-head">
                    <i className="fs-icon fa-solid fa-file-pdf" />
                    <h4>Resume Upload</h4>
                  </div>
                  <div className="fs-body">
                    <div
                      className={`upload-zone${dragActive ? " drag-active" : ""}`}
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragActive(true);
                      }}
                      onDragLeave={() => setDragActive(false)}
                      onDrop={handleDrop}
                    >
                      <i className="fa-solid fa-cloud-arrow-up" />
                      <p>
                        <strong>Click to upload</strong> or drag and drop
                        <br />
                        PDF, DOC, DOCX — max 5MB
                      </p>
                    </div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept=".pdf,.doc,.docx"
                      style={{ display: "none" }}
                      onChange={(e) => handleFileSelect(e.target.files[0])}
                    />
                    {resumeFile && (
                      <div className="file-preview">
                        <i className="fa-solid fa-file-pdf" />
                        <div className="file-preview-info">
                          <div className="file-preview-name">{resumeFile.name}</div>
                          <div className="file-preview-size">
                            {(resumeFile.size / 1024 / 1024).toFixed(2)} MB
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn-ghost btn-xs"
                          style={{ color: "var(--red)" }}
                          onClick={removeFile}
                        >
                          <i className="fa-solid fa-times" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Skills ── */}
                <div className="fs-recuriment" id="s-skills">
                  <div className="fs-head">
                    <i className="fs-icon fa-solid fa-tags" />
                    <h4>Skills</h4>
                  </div>
                  <div className="fs-body">
                    <div className="fg">
                      <label>
                        Skills <span className="req">*</span>
                      </label>
                      <div className="tag-input-wrap">
                        {formik.values.skills.map((skill, idx) => (
                          <span className="tag-item" key={skill + idx}>
                            {skill} <button type="button" onClick={() => removeSkill(idx)}>×</button>
                          </span>
                        ))}
                        <input
                          className="tag-text-input"
                          placeholder="Type and press Enter…"
                          value={skillInput}
                          onChange={(e) => setSkillInput(e.target.value)}
                          onKeyDown={addSkill}
                          onBlur={() => formik.setFieldTouched("skills", true)}
                        />
                      </div>
                      <div className="tag-hint">Press Enter or comma to add each skill</div>
                      {err("skills") && <div className="field-error">{err("skills")}</div>}
                    </div>
                  </div>
                </div>

                {/* ── Experience ── */}
                <div className="fs-recuriment" id="s-exp">
                  <div className="fs-head">
                    <i className="fs-icon fa-solid fa-briefcase" />
                    <h4>Work Experience</h4>
                  </div>
                  <div className="fs-body">
                    <FormikProvider value={formik}>
                      <FieldArray name="experience">
                        {({ push, remove }) => (
                          <>
                            <div id="expList">
                              {formik.values.experience.map((exp, idx) => (
                                <div className="exp-block" key={idx}>
                                  {formik.values.experience.length > 1 && (
                                    <button
                                      type="button"
                                      className="exp-remove"
                                      title="Remove"
                                      onClick={() => remove(idx)}
                                    >
                                      <i className="fa-solid fa-times" />
                                    </button>
                                  )}
                                  <div className="form-row form-row-2">
                                    <div className="fg">
                                      <label>
                                        Company <span className="req">*</span>
                                      </label>
                                      <input
                                        className="og-input"
                                        placeholder="Company name"
                                        name={`experience.${idx}.company`}
                                        value={exp.company}
                                        onChange={formik.handleChange}
                                      />
                                    </div>
                                    <div className="fg">
                                      <label>
                                        Role / Designation <span className="req">*</span>
                                      </label>
                                      <input
                                        className="og-input"
                                        placeholder="Senior Developer"
                                        name={`experience.${idx}.role`}
                                        value={exp.role}
                                        onChange={formik.handleChange}
                                      />
                                    </div>
                                  </div>
                                  <div className="form-row form-row-2" style={{ marginBottom: 0 }}>
                                    <div className="fg">
                                      <label>From</label>
                                      <input
                                        type="month"
                                        className="og-input"
                                        name={`experience.${idx}.from`}
                                        value={exp.from}
                                        onChange={formik.handleChange}
                                      />
                                    </div>
                                    <div className="fg">
                                      <label>
                                        To <span className="label-optional">leave blank if current</span>
                                      </label>
                                      <input
                                        type="month"
                                        className="og-input"
                                        name={`experience.${idx}.to`}
                                        value={exp.to}
                                        onChange={formik.handleChange}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <button
                              type="button"
                              className="btn btn-outline btn-sm"
                              style={{ marginTop: ".75rem" }}
                              onClick={() => push({ company: "", role: "", from: "", to: "" })}
                            >
                              <i className="fa-solid fa-plus" /> Add Another
                            </button>
                          </>
                        )}
                      </FieldArray>
                    </FormikProvider>
                  </div>
                </div>

                {/* ── Education ── */}
                <div className="fs-recuriment" id="s-edu">
                  <div className="fs-head">
                    <i className="fs-icon fa-solid fa-graduation-cap" />
                    <h4>Education</h4>
                  </div>
                  <div className="fs-body">
                    <FormikProvider value={formik}>
                      <FieldArray name="education">
                        {({ push, remove }) => (
                          <>
                            <div id="eduList">
                              {formik.values.education.map((edu, idx) => (
                                <div className="exp-block" key={idx}>
                                  {formik.values.education.length > 1 && (
                                    <button
                                      type="button"
                                      className="exp-remove"
                                      onClick={() => remove(idx)}
                                    >
                                      <i className="fa-solid fa-times" />
                                    </button>
                                  )}
                                  <div className="form-row form-row-2">
                                    <div className="fg">
                                      <label>Degree / Qualification</label>
                                      <input
                                        className="og-input"
                                        placeholder="B.Tech Computer Science"
                                        name={`education.${idx}.degree`}
                                        value={edu.degree}
                                        onChange={formik.handleChange}
                                      />
                                    </div>
                                    <div className="fg">
                                      <label>Institution</label>
                                      <input
                                        className="og-input"
                                        placeholder="Delhi Technological University"
                                        name={`education.${idx}.institution`}
                                        value={edu.institution}
                                        onChange={formik.handleChange}
                                      />
                                    </div>
                                  </div>
                                  <div className="form-row form-row-2" style={{ marginBottom: 0 }}>
                                    <div className="fg">
                                      <label>Year of Passing</label>
                                      <input
                                        type="number"
                                        className="og-input"
                                        placeholder="2019"
                                        min="1990"
                                        max="2030"
                                        name={`education.${idx}.year`}
                                        value={edu.year}
                                        onChange={formik.handleChange}
                                      />
                                    </div>
                                    <div className="fg">
                                      <label>Grade / CGPA</label>
                                      <input
                                        className="og-input"
                                        placeholder="8.2 / 10"
                                        name={`education.${idx}.grade`}
                                        value={edu.grade}
                                        onChange={formik.handleChange}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <button
                              type="button"
                              className="btn btn-outline btn-sm"
                              style={{ marginTop: ".75rem" }}
                              onClick={() => push({ degree: "", institution: "", year: "", grade: "" })}
                            >
                              <i className="fa-solid fa-plus" /> Add Another
                            </button>
                          </>
                        )}
                      </FieldArray>
                    </FormikProvider>
                  </div>
                </div>

                {/* ── Links ── */}
                <div className="fs-recuriment" id="s-links">
                  <div className="fs-head">
                    <i className="fs-icon fa-solid fa-globe" />
                    <h4>Online Profiles &amp; Links</h4>
                  </div>
                  <div className="fs-body">
                    <div className="form-row form-row-1">
                      <div className="fg">
                        <label>
                          <i className="fa-brands fa-linkedin icon-linkedin" />
                          LinkedIn URL
                        </label>
                        <input
                          className={`og-input${err("linkedin") ? " has-error" : ""}`}
                          placeholder="https://linkedin.com/in/username"
                          name="linkedin"
                          value={formik.values.linkedin}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {err("linkedin") && <div className="field-error">{err("linkedin")}</div>}
                      </div>
                    </div>
                    <div className="form-row form-row-1">
                      <div className="fg">
                        <label>
                          <i className="fa-brands fa-github icon-github" />
                          GitHub URL
                        </label>
                        <input
                          className={`og-input${err("github") ? " has-error" : ""}`}
                          placeholder="https://github.com/username"
                          name="github"
                          value={formik.values.github}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {err("github") && <div className="field-error">{err("github")}</div>}
                      </div>
                    </div>
                    <div className="form-row form-row-1">
                      <div className="fg">
                        <label>
                          <i className="fa-solid fa-globe icon-portfolio" />
                          Portfolio / Website
                        </label>
                        <input
                          className={`og-input${err("portfolio") ? " has-error" : ""}`}
                          placeholder="https://yoursite.com"
                          name="portfolio"
                          value={formik.values.portfolio}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {err("portfolio") && <div className="field-error">{err("portfolio")}</div>}
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Link to Job ── */}
                <div className="fs-recuriment" id="s-job">
                  <div className="fs-head">
                    <i className="fs-icon fa-solid fa-link" />
                    <h4>
                      Link to Job <span className="fs-head-note">(optional)</span>
                    </h4>
                  </div>
                  <div className="fs-body">
                    <div className="fg">
                      <label>Select Job</label>
                      <select
                        className="og-select"
                        name="jobId"
                        value={formik.values.jobId}
                        onChange={formik.handleChange}
                      >
                        <option value="">None — add to database only</option>
                        {JOB_OPTIONS.map((j) => (
                          <option key={j.id} value={j.label}>
                            {j.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="fg">
                      <label>Initial Pipeline Stage</label>
                      <select
                        className="og-select"
                        name="initialStage"
                        value={formik.values.initialStage}
                        onChange={formik.handleChange}
                      >
                        {STAGE_OPTIONS.map((s) => (
                          <option key={s.id} value={s.label}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="info-box">
                      <i className="fa-solid fa-circle-info" />
                      Candidate will be saved to the shared database and optionally added to the
                      selected job pipeline.
                    </div>
                  </div>
                </div>

                <div className="spacer-bottom" />
              </div>
            </div>

            <div className="form-footer">
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => setDiscardOpen(true)}
              >
                Discard
              </button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>
                <i className="fa-solid fa-check" /> {submitting ? "Saving…" : "Save Candidate"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* ── Discard Modal ── */}
      <div
        className={`modal-overlay${discardOpen ? " open" : ""}`}
        onClick={(e) => e.target === e.currentTarget && setDiscardOpen(false)}
      >
        <div className="modal-box">
          <div className="modal-head">
            <div className="modal-icon modal-icon-danger">
              <i className="fa-solid fa-triangle-exclamation" />
            </div>
            <div className="modal-title-wrap">
              <div className="modal-title">Discard changes?</div>
            </div>
            <button className="modal-close-btn" onClick={() => setDiscardOpen(false)}>
              <i className="fa-solid fa-times" />
            </button>
          </div>
          <div className="modal-body">
            <p className="modal-body-text">All entered data will be lost.</p>
          </div>
          <div className="modal-foot">
            <button className="btn btn-secondary btn-sm" onClick={() => setDiscardOpen(false)}>
              Keep editing
            </button>
            <button
              className="btn btn-danger btn-sm"
              onClick={() => navigate("recruitment-candidates.html")}
            >
              <i className="fa-solid fa-trash" /> Discard
            </button>
          </div>
        </div>
      </div>
    </>
  );
}